import React, { useEffect } from 'react';

import { type DOMNode, domToReact, type Element } from 'html-react-parser';

import { reportUnhandledContent } from 'helpers/reportToSentry';

import { detectChildType, isElement } from './detectInlineType';
import { InlineImage } from './InlineImage/InlineImage';
import { InlineMap } from './InlineMap/InlineMap';
import { InlineVideo } from './InlineVideo/InlineVideo';
import { InlineYouTube } from './InlineYouTube/InlineYouTube';

export interface InlineContentRendererProps {
  node: Element;
}

type InlineGroup =
  | { type: 'image'; elements: Element[] }
  | { type: 'map' | 'youtube' | 'video' | 'figure'; element: Element };

function describeElement(element: Element): string {
  const classes = element.attribs?.class?.trim().split(/\s+/).join('.');
  return classes ? `${element.name}.${classes}` : element.name;
}

function groupInlineChildren(node: Element) {
  const children = (node.children as DOMNode[]).filter(isElement);
  const groups: InlineGroup[] = [];
  const unsupported: Element[] = [];
  let images: Element[] = [];

  const flushImages = () => {
    if (images.length === 0) return;
    groups.push({ type: 'image', elements: [...images] });
    images = [];
  };

  for (const child of children) {
    const type = detectChildType(child);
    if (type === 'image') {
      images.push(child);
    } else {
      flushImages();
      if (type === 'unsupported') unsupported.push(child);
      else groups.push({ type, element: child });
    }
  }
  flushImages();
  return { groups, unsupported, isEmpty: children.length === 0 };
}

export function InlineContentRenderer({ node }: InlineContentRendererProps) {
  const { groups, unsupported, isEmpty } = groupInlineChildren(node);

  useEffect(() => {
    if (isEmpty) {
      reportUnhandledContent({ component: 'InlineContent', reason: 'empty-inline-block' });
    }
    unsupported.forEach((child) => {
      reportUnhandledContent({
        component: 'InlineContent',
        reason: 'unsupported-inline-child',
        variant: describeElement(child),
        extra: { attribs: child.attribs },
      });
    });
  });

  if (groups.length === 0) return null;

  return (
    <>
      {groups.map((group, i) => {
        if (group.type === 'image')
          return group.elements.map((el, j) => {
            return <InlineImage key={`${i}-${j}`} element={el} />;
          });
        if (group.type === 'map') return <InlineMap key={i} element={group.element} />;
        if (group.type === 'youtube') return <InlineYouTube key={i} element={group.element} />;
        if (group.type === 'figure')
          return <React.Fragment key={i}>{domToReact([group.element] as DOMNode[])}</React.Fragment>;
        return <InlineVideo key={i} element={group.element} />;
      })}
    </>
  );
}
